// Pure display helpers for the worker profile panel. Kept React-free so the
// label/threshold logic is unit-testable in isolation.
import type { ColorsType } from '@common/tokens';
import type { ScheduleTeamMemberType } from '@load-balancing/types';

// Employment / shift labels are shared with the Team card; re-export the
// canonical helpers so the two surfaces can't drift.
export {
  getEmploymentLabel,
  getShiftPatternLabel,
  getShiftTypeLabel,
} from '@load-balancing/services';

// Charge eligibility is only meaningful for non-charge roles. `chargeRole` is the service
// line's configured charge-role abbreviation (data-driven); when it's undefined we can't
// identify the charge role, so eligibility falls back to the worker's own flag.
export function getChargeEligibleLabel(
  member: ScheduleTeamMemberType,
  chargeRole?: string
): string {
  return member.role !== chargeRole && member.chargeEligible ? 'Yes' : '—';
}

/** FTE attainment readout below the progress bar; "—" until hours are populated. */
export function getFteRemainingLabel(member: ScheduleTeamMemberType): string {
  if (member.remainingHours === null) return '—';
  if (member.remainingHours < 0) {
    return `${Math.abs(member.remainingHours)}h over FTE`;
  }
  if (member.remainingHours === 0) return 'At FTE';
  return `${member.remainingHours}h remaining`;
}

/**
 * Bar color: coral when over FTE; otherwise undefined so ProgressBar keeps its
 * gray-below / auto-green-at-target behavior (and while hours are null).
 */
export function getFteBarColor(
  member: ScheduleTeamMemberType
): ColorsType | undefined {
  if (member.remainingHours === null) return undefined;
  return member.remainingHours < 0 ? 'coral' : undefined;
}
